import { useMemo } from 'react';

import Fuse from 'fuse.js';
import { useLazyLoadQuery } from 'react-relay';

import { useSearchSuggestionsContentfulQuery } from '@/__generated_contentful__/useSearchSuggestionsContentfulQuery.graphql';

import { CategoryTemplateOrder } from '../../../config/category-template-order';
import {
	FUSE_OPTIONS,
	parseCategorySearchItems,
	parseTemplateSearchItems,
	UseSearchSuggestionsQuery,
} from '../../hooks/contentful-relay-hooks/use-search-suggestions-contentful';
import { useQueryLocale } from '../../hooks/use-query-locale';

import { CategorySearchSuggestion } from './template-category-search-results-contentful-relay';
import { TemplateInitialSearchResults } from './template-initial-search-results-contentful-relay';

function parseSearchQueryFromSuggestions(
	templateMatches: TemplateSearchSuggestion[],
	categoryMatches: CategorySearchSuggestion[],
): {
	whereClause: Record<string, string[]>;
} {
	const filters: Record<string, string[]> = {
		templateIds: [],
		urlSlugs: [],
		categoryUrlSlugs: [],
	};

	const templateIds = templateMatches
		.map((result) => result.id)
		.filter((id) => typeof id === 'string');

	if (templateIds.length > 0) {
		filters['urlSlugs'] = templateIds;
	}

	const categoryIds = categoryMatches
		.map((result) => result.id)
		.filter((id) => typeof id === 'string');

	if (categoryIds.length > 0) {
		filters['categoryUrlSlugs'] = categoryIds;
	}

	return {
		whereClause: filters,
	};
}

export type TemplateSearchSuggestion = {
	type: 'template';
	id: string;
	name?: string | null;
	description?: string | null;
	contributor?: string | null;
	contributorOrganization?: string | null;
	customTag?: string[] | null;
	templateType?: string | null;
};

export type TemplateFuzzySearchResultsProps = {
	searchTerm: string | TemplateSearchSuggestion;
	/**
	 * A unique key to identify the search term.
	 * This is required to force a new instance
	 * of `TemplatePaginatedSearchResults`
	 * when the search term changes.
	 */
	searchKey: string;
};

export const TemplateFuzzySearchResults = ({
	searchTerm,
	searchKey,
}: TemplateFuzzySearchResultsProps) => {
	const locale = useQueryLocale();
	const suggestionData = useLazyLoadQuery<useSearchSuggestionsContentfulQuery>(
		UseSearchSuggestionsQuery,
		{ locale },
	);

	const fuse = useMemo(() => {
		return new Fuse(
			[
				...parseTemplateSearchItems(suggestionData.productTemplateCollection),
				...parseCategorySearchItems(suggestionData.categories),
			],
			FUSE_OPTIONS,
		);
	}, [suggestionData]);

	const [templateMatches, categoryMatches] = useMemo(() => {
		const matches = fuse
			.search(typeof searchTerm === 'string' ? searchTerm : (searchTerm.name ?? searchTerm.id))
			// Sort by score in descending order
			// Lower scores are a better match
			.sort((a, b) => (a.score ?? Infinity) - (b.score ?? Infinity))
			.map((result) => result.item);

		return matches
			.slice(0, 35) // Get the top 35 results
			.reduce<[TemplateSearchSuggestion[], CategorySearchSuggestion[]]>(
				(acc, result) => {
					if (result.type === 'template') {
						acc[0].push(result);
					} else if (result.type === 'category') {
						acc[1].push(result);
					}
					return acc;
				},
				[[], []],
			);
	}, [fuse, searchTerm]);

	const sortLookup = useMemo(() => {
		const lookup: Record<string, number> = {};
		let rank = 1;

		// Rank template matches before category matches.
		for (const template of templateMatches) {
			if (template.type === 'template') {
				lookup[template.id] = rank;
				rank++;
			}
		}
		// Use hardcoded category order for ranking category matches.
		for (const category of categoryMatches) {
			if (category.type === 'category') {
				const categoryLookup =
					CategoryTemplateOrder[category.id as keyof typeof CategoryTemplateOrder];
				if (categoryLookup) {
					for (const slug in categoryLookup) {
						// Exclude templates that are already ranked.
						if (!(slug in lookup)) {
							lookup[slug] = rank;
							rank++;
						}
					}
				}
			}
		}

		return lookup;
	}, [categoryMatches, templateMatches]);

	const searchQuery = useMemo(() => {
		return parseSearchQueryFromSuggestions(templateMatches, categoryMatches);
	}, [categoryMatches, templateMatches]);

	return (
		<TemplateInitialSearchResults
			searchKey={searchKey}
			urlSlugs={searchQuery.whereClause?.['urlSlugs'] ?? []}
			templateIds={searchQuery.whereClause?.['templateIds'] ?? []}
			categoryUrlSlugs={searchQuery.whereClause?.['categoryUrlSlugs'] ?? []}
			limit={Object.keys(sortLookup).length}
			skip={0}
			locale={locale}
			sortLookup={sortLookup}
		/>
	);
};
